/**
 * Heating Step - Form Component
 *
 * Step 3 of the Energy Certificate Simulator wizard.
 * Collects heating system information: source, age, hot water.
 */

import {
  Stack,
  SegmentedControl,
  Text,
  Paper,
  Group,
  ThemeIcon,
  Badge,
  Switch,
  Collapse,
  Slider,
  NumberInput,
} from '@mantine/core';
import {
  IconFlame,
  IconClock,
  IconDroplet,
  IconFlameOff,
  IconAdjustments,
} from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import type { UseFormReturnType } from '@mantine/form';
import type { SimulatorInput, HeatingSystem, SystemAge, HotWaterSource } from '../../types';

// =============================================================================
// CONSTANTS
// =============================================================================

const HEATING_SYSTEMS: { value: HeatingSystem; label: string; category: string; wi: string; description: string }[] = [
  { value: 'gas_condensing', label: 'Kocioł gazowy kondensacyjny', category: 'Gaz', wi: '1.1', description: 'Najwyższa sprawność ~97%, odzysk ciepła spalin' },
  { value: 'gas_traditional', label: 'Kocioł gazowy tradycyjny', category: 'Gaz', wi: '1.1', description: 'Sprawność ~85-90%, starszy typ kotła' },
  { value: 'heat_pump_ground', label: 'Pompa ciepła gruntowa', category: 'Pompy ciepła', wi: '~0.6', description: 'COP 4-5, ciepło z gruntu, najefektywniejsza' },
  { value: 'heat_pump_air', label: 'Pompa ciepła powietrze-woda', category: 'Pompy ciepła', wi: '~0.7', description: 'COP 3-4, ciepło z powietrza zewnętrznego' },
  { value: 'district_heating', label: 'Ciepło sieciowe', category: 'Sieć ciepłownicza', wi: '0.8-1.3', description: 'Ciepłownia miejska, zależne od źródła' },
  { value: 'biomass', label: 'Kocioł na biomasę (pellet/drewno)', category: 'Biomasa', wi: '0.2', description: 'Niski ślad węglowy, odnawialny' },
  { value: 'coal', label: 'Kocioł węglowy', category: 'Węgiel', wi: '1.1', description: 'Sprawność ~70-80%, wysokie emisje CO2' },
  { value: 'stove', label: 'Piec kaflowy / kominek', category: 'Węgiel', wi: '1.1', description: 'Ogrzewanie punktowe, niska sprawność ~60%' },
  { value: 'electric_floor', label: 'Ogrzewanie elektryczne podłogowe', category: 'Elektryczne', wi: '2.5', description: 'Komfort cieplny, wysokie koszty eksploatacji' },
  { value: 'electric_radiators', label: 'Ogrzewanie elektryczne (grzejniki)', category: 'Elektryczne', wi: '2.5', description: 'Proste w montażu, drogie w użytkowaniu' },
];

const SYSTEM_AGES: { value: SystemAge; label: string; description: string }[] = [
  { value: 'new', label: 'Nowy', description: 'Mniej niż 5 lat' },
  { value: 'medium', label: 'Średni', description: '5-15 lat' },
  { value: 'old', label: 'Stary', description: 'Ponad 15 lat' },
];

const HOT_WATER_SOURCES: { value: HotWaterSource; label: string; description: string }[] = [
  { value: 'combined', label: 'Wspólne z c.o.', description: 'Ten sam kocioł co ogrzewanie' },
  { value: 'gas', label: 'Gazowe', description: 'Oddzielny podgrzewacz gazowy' },
  { value: 'electric', label: 'Elektryczne', description: 'Bojler elektryczny' },
  { value: 'solar', label: 'Solarne', description: 'Kolektory słoneczne + wspomaganie' },
];

const SECONDARY_HEATING_SYSTEMS: { value: HeatingSystem; label: string }[] = [
  { value: 'gas_condensing', label: 'Kocioł gazowy kondensacyjny' },
  { value: 'gas_traditional', label: 'Kocioł gazowy tradycyjny' },
  { value: 'heat_pump_air', label: 'Pompa ciepła powietrze-woda' },
  { value: 'electric_radiators', label: 'Grzejniki elektryczne' },
  { value: 'electric_floor', label: 'Ogrzewanie podłogowe elektryczne' },
  { value: 'biomass', label: 'Kocioł na biomasę' },
  { value: 'stove', label: 'Piec kaflowy / kominek' },
  { value: 'coal', label: 'Kocioł węglowy' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export interface HeatingStepProps {
  /** Mantine form instance */
  form: UseFormReturnType<SimulatorInput>;
}

/**
 * Heating system data collection step
 *
 * Collects:
 * - Primary heating system type
 * - Heating system age
 * - Domestic hot water source
 */
export function HeatingStep({ form }: HeatingStepProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasSecondaryHeating = form.values.hasSecondaryHeating;

  // Sync form value with visual default when secondary heating share slider becomes visible.
  // The Slider displays `form.values.secondaryHeatingShare ?? 20` but the form value
  // remains undefined until the user interacts, causing validation to fail silently.
  useEffect(() => {
    if (hasSecondaryHeating && form.values.secondaryHeatingShare == null) {
      form.setFieldValue('secondaryHeatingShare', 20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSecondaryHeating]);

  return (
    <Stack gap="lg">
      {/* Heating system */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="red">
              <IconFlame size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Główne źródło ogrzewania
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            System używany do ogrzewania budynku przez większość sezonu grzewczego
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={HEATING_SYSTEMS.map((system) => ({
              value: system.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Text size="sm" ta="left">{system.label}</Text>
                    <Text size="xs" c="dimmed" ta="left">{system.description}</Text>
                  </Stack>
                  <Badge size="xs" variant="light" color="gray">
                    wi={system.wi}
                  </Badge>
                </Group>
              ),
            }))}
            {...form.getInputProps('heatingSystem')}
            aria-label="Główne źródło ogrzewania"
          />
        </Stack>
      </Paper>

      {/* System age */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="yellow">
              <IconClock size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Wiek instalacji grzewczej
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Przybliżony wiek kotła lub głównego urządzenia grzewczego
          </Text>
          <SegmentedControl
            fullWidth
            data={SYSTEM_AGES.map((age) => ({
              value: age.value,
              label: (
                <Stack gap={2} align="center">
                  <Text size="sm">{age.label}</Text>
                  <Text size="xs" c="dimmed">
                    {age.description}
                  </Text>
                </Stack>
              ),
            }))}
            {...form.getInputProps('systemAge')}
            aria-label="Wiek instalacji grzewczej"
          />
        </Stack>
      </Paper>

      {/* Secondary heating toggle */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="grape">
              <IconFlameOff size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Dodatkowe źródło ogrzewania
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Czy budynek jest ogrzewany jeszcze innym źródłem ciepła?
          </Text>
          <Switch
            label="Mam dodatkowe źródło ogrzewania"
            checked={form.values.hasSecondaryHeating}
            onChange={(event) => form.setFieldValue('hasSecondaryHeating', event.currentTarget.checked)}
            aria-label="Dodatkowe źródło ogrzewania"
          />
        </Stack>
      </Paper>

      {/* Secondary heating details (conditional) */}
      <Collapse in={form.values.hasSecondaryHeating}>
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Typ dodatkowego źródła
              </Text>
              <SegmentedControl
                fullWidth
                orientation="vertical"
                data={SECONDARY_HEATING_SYSTEMS
                  .filter(s => s.value !== form.values.heatingSystem)
                  .map((system) => ({
                    value: system.value,
                    label: (
                      <Text size="sm" ta="left" px="xs">{system.label}</Text>
                    ),
                  }))}
                value={form.values.secondaryHeatingSystem ?? ''}
                onChange={(value) => form.setFieldValue('secondaryHeatingSystem', value as HeatingSystem)}
                aria-label="Dodatkowe źródło ogrzewania"
              />
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Udział dodatkowego źródła
              </Text>
              <Text size="xs" c="dimmed">
                Jaki procent ogrzewania zapewnia dodatkowe źródło?
              </Text>
              <Slider
                min={10}
                max={50}
                step={5}
                marks={[
                  { value: 10, label: '10%' },
                  { value: 20, label: '20%' },
                  { value: 30, label: '30%' },
                  { value: 40, label: '40%' },
                  { value: 50, label: '50%' },
                ]}
                label={(value) => `${value}%`}
                thumbLabel="Udział dodatkowego źródła"
                value={form.values.secondaryHeatingShare ?? 20}
                onChange={(value) => form.setFieldValue('secondaryHeatingShare', value)}
                aria-label="Udział dodatkowego źródła ogrzewania"
              />
            </Stack>
          </Paper>
        </Stack>
      </Collapse>

      {/* Hot water source */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="blue">
              <IconDroplet size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Źródło ciepłej wody użytkowej (CWU)
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Sposób podgrzewania wody do celów bytowych (prysznic, kran)
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={HOT_WATER_SOURCES.map((source) => ({
              value: source.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Text size="sm" ta="left">{source.label}</Text>
                    <Text size="xs" c="dimmed" ta="left">{source.description}</Text>
                  </Stack>
                </Group>
              ),
            }))}
            {...form.getInputProps('hotWaterSource')}
            aria-label="Źródło ciepłej wody użytkowej"
          />
        </Stack>
      </Paper>

      {/* Advanced hot water settings */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="gray">
              <IconAdjustments size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Zaawansowane ustawienia CWU
            </Text>
          </Group>
          <Switch
            label="Pokaż szczegółowe parametry ciepłej wody"
            checked={showAdvanced}
            onChange={(event) => setShowAdvanced(event.currentTarget.checked)}
            aria-label="Zaawansowane ustawienia CWU"
          />
        </Stack>
      </Paper>

      <Collapse in={showAdvanced}>
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Stack gap="md">
              <NumberInput
                label="Dzienne zużycie CWU na osobę [l/dzień]"
                description="Domyślnie: 40 l/os/dzień (norma PN-EN 15316)"
                placeholder="40"
                min={20}
                max={100}
                step={5}
                value={form.values.dailyHotWaterPerPerson ?? ''}
                onChange={(value) => form.setFieldValue('dailyHotWaterPerPerson', value === '' ? undefined : Number(value))}
                aria-label="Dzienne zużycie CWU na osobę"
              />
              <NumberInput
                label="Temperatura ciepłej wody [°C]"
                description="Domyślnie: 55°C (norma sanitarna)"
                placeholder="55"
                min={40}
                max={70}
                step={1}
                value={form.values.hotWaterTemp ?? ''}
                onChange={(value) => form.setFieldValue('hotWaterTemp', value === '' ? undefined : Number(value))}
                aria-label="Temperatura ciepłej wody"
              />
              <NumberInput
                label="Temperatura wody zimnej [°C]"
                description="Domyślnie: 10°C (średnia roczna wody wodociągowej)"
                placeholder="10"
                min={4}
                max={20}
                step={1}
                value={form.values.coldWaterTemp ?? ''}
                onChange={(value) => form.setFieldValue('coldWaterTemp', value === '' ? undefined : Number(value))}
                aria-label="Temperatura wody zimnej"
              />
            </Stack>
          </Paper>
        </Stack>
      </Collapse>

      {/* Info about wi coefficient */}
      <Paper p="md" bg="orange.0">
        <Text size="xs" c="orange.8">
          <strong>Współczynnik wi</strong> określa nakład energii pierwotnej
          potrzebny do dostarczenia jednostki energii końcowej. Im niższy, tym
          lepiej dla środowiska. Pompy ciepła i biomasa mają najniższe wartości.
        </Text>
      </Paper>
    </Stack>
  );
}

export default HeatingStep;
